import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import settingsService from '../../services/settings.service';
import { getError } from '../../utils';
import RichHtmlEditor from '../RichHtmlEditor/RichHtmlEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export default function PublicPagesConfig() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contactPageHtml, setContactPageHtml] = useState('');
  const [termsPageHtml, setTermsPageHtml] = useState('');
  const [privacyPolicyHtml, setPrivacyPolicyHtml] = useState('');
  const [sellerFieldLabel, setSellerFieldLabel] = useState('');
  const [sellerFieldPlaceholder, setSellerFieldPlaceholder] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const s = await settingsService.getPublicSettings();
        setContactPageHtml(s.contactPageHtml || '');
        setTermsPageHtml(s.termsPageHtml || '');
        setPrivacyPolicyHtml(s.privacyPolicyHtml || '');
        setSellerFieldLabel(s.sellerFieldLabel || '');
        setSellerFieldPlaceholder(s.sellerFieldPlaceholder || '');
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsService.updateSettings({
        contactPageHtml,
        termsPageHtml,
        privacyPolicyHtml,
        sellerFieldLabel,
        sellerFieldPlaceholder,
      });
      toast.success(t('admin.publicPagesSaved'));
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='text-center py-4'>
        <Loader2 className='h-4 w-4 animate-spin' />
      </div>
    );
  }

  return (
    <div className='public-pages-config'>
      <p className='text-sm text-muted-foreground mb-3'>{t('admin.publicPagesHelp')}</p>
      <div className='mb-4'>
        <label className='block text-sm font-medium text-foreground'>
          {t('admin.contactPageHtml')}
        </label>
        <RichHtmlEditor
          value={contactPageHtml}
          onChange={setContactPageHtml}
          aria-label={t('admin.contactPageHtml')}
        />
      </div>
      <div className='mb-4'>
        <label className='block text-sm font-medium text-foreground'>
          {t('admin.termsPageHtml')}
        </label>
        <RichHtmlEditor
          className='rich-html-editor--terms'
          value={termsPageHtml}
          onChange={setTermsPageHtml}
          aria-label={t('admin.termsPageHtml')}
          minHeight={320}
        />
      </div>
      <div className='mb-4 space-y-3'>
        <label className='block text-sm font-medium text-foreground'>Política de privacidad</label>
        <p className='text-xs text-muted-foreground'>
          Incluí los datos del cliente, contacto y tratamiento de Analytics y Clarity.
        </p>
        <RichHtmlEditor
          value={privacyPolicyHtml}
          onChange={setPrivacyPolicyHtml}
          aria-label='Política de privacidad'
          minHeight={320}
        />
      </div>
      <div className='mb-4 space-y-3'>
        <div>
          <p className='text-sm font-medium text-foreground'>{t('admin.sellerFieldTitle')}</p>
          <p className='text-xs text-muted-foreground'>{t('admin.sellerFieldHelp')}</p>
        </div>
        <div className='space-y-1.5'>
          <label htmlFor='sellerFieldLabel' className='block text-sm text-foreground'>
            {t('admin.sellerFieldLabel')}
          </label>
          <Input
            id='sellerFieldLabel'
            value={sellerFieldLabel}
            placeholder={t('reservation.sellerNameOptional')}
            onChange={e => setSellerFieldLabel(e.target.value)}
          />
        </div>
        <div className='space-y-1.5'>
          <label htmlFor='sellerFieldPlaceholder' className='block text-sm text-foreground'>
            {t('admin.sellerFieldPlaceholder')}
          </label>
          <Input
            id='sellerFieldPlaceholder'
            value={sellerFieldPlaceholder}
            placeholder={t('reservation.sellerNamePlaceholder')}
            onChange={e => setSellerFieldPlaceholder(e.target.value)}
          />
        </div>
      </div>
      <Button type='button' disabled={saving} onClick={() => void handleSave()}>
        {saving ? (
          <>
            <Loader2 className='h-4 w-4 animate-spin mr-2' />
            {t('common.loading')}
          </>
        ) : (
          t('common.save')
        )}
      </Button>
    </div>
  );
}
